import { AHIcon, ListItem, ListItemProps, useLayoutContext } from "arkh";
import { Platform } from "react-native";
import Animated, {
  SharedValue,
  useAnimatedReaction,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import React, { PropsWithChildren, useMemo } from "react";
import * as Haptic from "expo-haptics";
import { Positions } from "../../Positions";
import { scheduleOnRN } from "react-native-worklets";

export type ReordrableProps = PropsWithChildren<{
  positions: SharedValue<Positions>;
  activeItem: SharedValue<string | null>;
  id: string;
  height: number;
  listHeight: SharedValue<number>;
  onReorder(order: Positions): void;
  disabled?: boolean;
  hasFooter?: boolean;
  iconPosition?: "left" | "right";
  onPress?: () => void;
}>;

const getIndex = (ty: number, height: number) => {
  "worklet";
  const y = Math.round(ty / height) * height;
  const index = Math.floor(Math.max(y, 0) / height);
  return index;
};

export type ReordrableListItemProps = ListItemProps & {
  reorderableListItemProps: ReordrableProps;
};

function clampWithRubberBand(value: number, min: number, max: number): number {
  "worklet";
  if (value >= min && value <= max) {
    return value;
  }

  const dimension = max - min;

  if (dimension === 0) {
    return min;
  }

  const isBelow = value < min;
  const distance = isBelow ? min - value : value - max;
  const boundary = isBelow ? min : max;
  const dampenedOffset = dimension * Math.log10(1 + distance / dimension);

  return isBelow ? boundary - dampenedOffset : boundary + dampenedOffset;
}
export function ReordrableListItem({
  reorderableListItemProps: {
    activeItem,
    height,
    id,
    onReorder,
    positions,
    onPress,
    disabled,
    iconPosition: _iconPosition,
    listHeight,
  },
  ...listItemProps
}: ReordrableListItemProps) {
  const layout = useLayoutContext();

  const position = useDerivedValue<number | null>(
    () => positions.value[id] ?? null
  );
  const y = useSharedValue((position.value ?? 0) * height);
  useAnimatedReaction(
    () => position.value,
    (idx) => {
      if (idx !== null) {
        y.value = withTiming(idx * height, { duration: 300 });
      }
    },
    [height]
  );

  const animatedStyle = useAnimatedStyle(() => {
    return {
      zIndex: activeItem.value === id ? 10 : 2,
      height,
      width: "100%",
      position: "absolute",
      top: 0,
      left: 0,
      transform: [
        {
          translateY: y.value,
        },
      ],
    };
  });

  const ctx = useSharedValue({ y: 0 });

  const gesture = Gesture.Pan()
    .onStart(() => {
      ctx.value.y = (position.value ?? 0) * height;
      y.value = ctx.value.y;
      activeItem.value = id;
    })
    .onChange(({ translationY }) => {
      const newY = clampWithRubberBand(
        translationY + ctx.value.y,
        0,
        listHeight.value
      );

      y.value = newY;
      const newOrder = getIndex(y.value, height);
      const oldOrder = position.value;
      if (oldOrder !== null && newOrder !== oldOrder) {
        const idToSwap = Object.keys(positions.value).find(
          (key) => positions.value[key] === newOrder
        );
        if (idToSwap) {
          const newPositions = Object.assign({}, positions.value);
          newPositions[id] = newOrder;
          newPositions[idToSwap] = oldOrder;
          positions.value = newPositions;
        }
      }
    })
    .onEnd((e) => {
      const newPosition = positions.value[id];

      y.value = withSpring(newPosition * height, undefined, () => {
        activeItem.value = null;
        scheduleOnRN(onReorder, positions.value);
      });
      if (Platform.OS === "ios") {
        scheduleOnRN(Haptic.impactAsync, Haptic.ImpactFeedbackStyle.Light);
      }
    })
    .hitSlop({ left: 90, right: 90 })
    .activeOffsetY(0)
    .minDistance(10)
    .enabled(!disabled);

  const iconPosition = _iconPosition === "right" ? "right" : "left";

  const Icon = useMemo(() => {
    return (
      <Animated.View>
        <AHIcon type="drag" color="text-secondary" />
      </Animated.View>
    );
  }, []);
  return (
    <Animated.View style={animatedStyle}>
      <ListItem
        border="border-primary"
        isFree
        forceHeight={height - layout.space.small}
        disabled
        {...listItemProps}
        left={iconPosition === "left" ? Icon : undefined}
        right={
          <>
            {listItemProps.right}
            {iconPosition === "right" ? Icon : undefined}
          </>
        }
      />
      <GestureDetector gesture={gesture}>
        <Animated.View
          style={{
            position: "absolute",
            [iconPosition]: 0,
            top: 0,
            bottom: 0,
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
            paddingLeft: layout.space.medium,
            paddingRight: layout.space.medium,
            cursor: "grab" as any,

            width: "40%",
            minWidth: 100,
          }}
        ></Animated.View>
      </GestureDetector>
    </Animated.View>
  );
}

// export const ReordrableListItemRelativeIndicator = React.memo(() => (
//   <AHIcon type="drag" />
// ));

// export const ReordrableListItemFloatingIndicator = React.memo(() => {
//   const layout = useLayoutContext();
//   return (
//     <Animated.View
//       style={{
//         position: "absolute",
//         right: 0,
//         top: 0,
//         bottom: 0,
//         height: "100%",
//         alignItems: "center",
//         justifyContent: "center",
//         paddingRight: layout.space.medium,
//       }}
//     >
//       <AHIcon type="drag" />
//     </Animated.View>
//   );
// });
